import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { API } from '../../services/api';
const api = new API();

import { fetchInitData } from '../app/appSlice';
import { fetchUserInfo } from '../user/userSlice';

// First, create the thunk
export const login = createAsyncThunk(
  'auth/login',
  async (data, thunkAPI) => {
    try {
      const response = await api.login(data);

      if(response.status === 'ok') {

        await thunkAPI.dispatch(fetchUserInfo());
        await thunkAPI.dispatch(fetchInitData());

        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch(e) {
      // System error or status code >= 500
      console.error('auth/login', e);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (data, thunkAPI) => {
    const response = await api.logout();
    return response.data;
  }
);

export const checkAuth = createAsyncThunk(
  'auth/check',
  async (data, thunkAPI) => {
    try {
      const response = await api.getUserInfo();

      if(response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }
    } catch(e) {
      // System error or status code >= 500
      console.error('auth/check', e.message);

      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPw',
  async (data, thunkAPI) => {
    try {
      const stageTokenRequest = await api.getStageToken();

      if(stageTokenRequest.status === 'ok') {
        const response = await api.forgotPassword({username: data, token: stageTokenRequest.result});

        if(response.status === 'ok') {
          return response.result;
        } else {
          return thunkAPI.rejectWithValue(response);
        }
      } else {
        // Token endpoint not working
        return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
      }
    } catch(e) {
      console.error('auth/forgotPw', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
)

export const resetPassword = createAsyncThunk(
  'auth/resetPw',
  async (data, thunkAPI) => {
    try {
      const response = await api.changePassword(data);

      if(response.status === 'ok') {
        return response.result;
      } else {
        return thunkAPI.rejectWithValue(response);
      }

    } catch(e) {
      console.error('auth/resetPw', e.message);
      return thunkAPI.rejectWithValue('INTERNAL_SERVER_ERROR');
    }
  }
)

const initialState = {
  loggedIn: false,
  loginStatus: 'idle',
  loginError: null,
  forgotPwStatus: 'idle',
  forgotPwError: null,
  resetPwStatus: 'idle',
  resetPwError: null
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearLoginState: (state, action) => {
      state.loginStatus = 'idle';
    },
    clearForgotPwState: (state, action) => {
      state.forgotPwStatus = 'idle';
    },
    clearResetPwState: (state, action) => {
      state.resetPwStatus = 'idle';
    }
  },
  extraReducers: {
    [login.fulfilled]: (state, action) => {
      state.loginStatus = 'succeeded';
      state.loginError = null;
      state.loggedIn = true;
    },
    [login.pending]: (state, action) => {
      state.loginStatus = 'loading';
      state.loginError = null;
    },
    [login.rejected]: (state, action) => {
      state.loginStatus = 'failed';
      state.loginError = {
        message: action.payload.messages.join(',')
      };
    },

    [logout.fulfilled]: (state, action) => {
      return initialState;
    },

    [checkAuth.fulfilled]: (state, action) => {
      state.loginStatus = 'succeeded';
      state.loginError = null;
      state.loggedIn = true;
    },

    [forgotPassword.fulfilled]: (state, action) => {
      state.forgotPwStatus = 'succeeded';
      state.forgotPwError = null;
    },
    [forgotPassword.pending]: (state, action) => {
      state.forgotPwStatus = 'loading';
      state.forgotPwError = null;
    },
    [forgotPassword.rejected]: (state, action) => {
      state.forgotPwStatus = 'failed';
      state.forgotPwError = {
        message: action.payload.messages.join(', '),
        errors: action.payload.errors
      };
    },

    [resetPassword.fulfilled]: (state, action) => {
      state.resetPwStatus = 'succeeded';
      state.resetPwError = null;
    },
    [resetPassword.pending]: (state, action) => {
      state.resetPwStatus = 'loading';
      state.resetPwError = null;
    },
    [resetPassword.rejected]: (state, action) => {
      state.resetPwStatus = 'failed';
      state.resetPwError = {
        message: action.payload.messages.join(', '),
        errors: action.payload.errors
      };
    }
  }
});

export const {
  clearLoginState,
  clearForgotPwState,
  clearResetPwState
} = authSlice.actions;

export const selectLoggedIn = state => state.auth.loggedIn;

export const selectLoginStatus = state => ({
  loggedIn: state.auth.loggedIn,
  status: state.auth.loginStatus,
  error: state.auth.loginError
})

export const selectForgotPwStatus = state => ({
  status: state.auth.forgotPwStatus,
  error: state.auth.forgotPwError
});

export const selectResetPwStatus = state => ({
  status: state.auth.resetPwStatus,
  error: state.auth.resetPwError
});

export default authSlice.reducer;
